import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import {normalizeRotation} from '~/utils/math.js';

export function useSurfaceEditorPersistence({surfaces, project}) {
  const api = useSurfaceEditorApi();

  const lastPersistedSurfaces = shallowRef(cloneDeep(toRaw(surfaces.value)));

  //helpers
  function classifyChanges(prev, curr) {
    const prevMap = new Map(prev.map(e => [e.id, e]));
    const currMap = new Map(curr.map(e => [e.id, e]));

    const added = [];
    const removed = [];
    const updated = [];

    for (const [id, current] of currMap) {
      const previous = prevMap.get(id);

      if (!previous) {
        added.push(current);
      } else if (!isEqual(previous, current)) {
        updated.push(current);
      }
    }

    for (const [id, previous] of prevMap) {
      if (!currMap.has(id)) {
        removed.push(previous);
      }
    }

    return { added, removed, updated };
  }

  function extractStableOrder(prevSurface, currSurface) {
    const prevIds = prevSurface.elements.map(e => e.id);
    const currIds = currSurface.elements.map(e => e.id);

    // Only elements that exist in BOTH snapshots
    const commonIds = prevIds.filter(id => currIds.includes(id));

    return {
      prevOrder: prevIds.filter(id => commonIds.includes(id)),
      currOrder: currIds.filter(id => commonIds.includes(id))
    };
  }

  function hasOrderChanged(prevOrder, currOrder) {
    if (prevOrder.length !== currOrder.length) return false;
    return !prevOrder.every((id, i) => id === currOrder[i]);
  }

  //persist methods
  function persistSurfaceChanges(prev, curr) {
    const prevToClassifyWith = prev.map(s => Object.assign({}, s, {
      elements: undefined,
      background: undefined
    }));
    const currToClassifyWith = curr.map(s => Object.assign({}, s, {
      elements: undefined,
      background: undefined
    }));

    const requests = [];

    const { added, removed, updated } = classifyChanges(prevToClassifyWith, currToClassifyWith);

    for (const surface of added) {
      const fullSurface = curr.find(s => s.id === surface.id);

      requests.push(
        api.createSurface({
          projectId: project.value.id,
          surfaceId: surface.id,
          width: surface.width,
          height: surface.height,
          backgroundElementId: fullSurface.background.id
        }).then(() => {
          if (!fullSurface) return;

          const requests = [];

          if (fullSurface.background) {
            requests.push(api.updateElement({
              projectId: project.value.id,
              surfaceId: surface.id,
              element: fullSurface.background
            }));
          }

          for (const el of fullSurface.elements) {
            requests.push(api.createElement({
              projectId: project.value.id,
              surfaceId: surface.id,
              element: Object.assign({}, el, {
                rotation: el.rotation && normalizeRotation(el.rotation)
              })
            }));
          }

          return Promise.all(requests);
        })
      );
    }

    for (const surface of removed) {
      requests.push(api.deleteSurface({
        projectId: project.value.id,
        surfaceId: surface.id
      }));
    }

    for (const surface of updated) {
      requests.push(api.updateSurface(surface.id));
    }

    return Promise.all(requests);
  }

  function persistElementChanges(prev, curr) {
    for (const surface of curr) {
      const prevSurface = prev.find(s => s.id === surface.id);

      if (!prevSurface) {
        continue;
      }

      const prevElements = [prevSurface.background, ...prevSurface.elements];
      const currElements = [surface.background, ...surface.elements];

      const { added, removed, updated } =
        classifyChanges(prevElements, currElements);

      const requests = [];

      for (const el of added) {
        requests.push(api.createElement({
          projectId: project.value.id,
          surfaceId: surface.id,
          element: Object.assign({}, el, {
            rotation: el.rotation && normalizeRotation(el.rotation)
          })
        }));
      }

      for (const el of removed) {
        requests.push(api.deleteElement({
          projectId: project.value.id,
          surfaceId: surface.id,
          element: el
        }));
      }

      for (const el of updated) {
        requests.push(api.updateElement({
          projectId: project.value.id,
          surfaceId: surface.id,
          element: Object.assign({}, el, {
            rotation: el.rotation && normalizeRotation(el.rotation)
          })
        }));
      }

      const { prevOrder, currOrder } =
        extractStableOrder(prevSurface, surface);

      if (hasOrderChanged(prevOrder, currOrder)) {
        requests.push(
          api.reorderElements({
            projectId: project.value.id,
            surfaceId: surface.id,
            elementIds: currOrder
          })
        );
      }

      return Promise.all(requests);
    }
  }

  return {
    async persist() {
      try {
        const prev = lastPersistedSurfaces.value;
        const curr = cloneDeep(toRaw(surfaces.value));

        await Promise.all([
          persistSurfaceChanges(prev, curr),
          persistElementChanges(prev, curr)
        ]);

        lastPersistedSurfaces.value = cloneDeep(curr);
      } catch (e) {
        //todo: remove
        console.log('PERSIST ERROR:', e);
        useEventBus('surface-editor-persist-error').emit();
      }
    },
    reset() {
      lastPersistedSurfaces.value = cloneDeep(toRaw(surfaces.value));
    }
  }

}
